/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { DivisionItem } from '../../hooks/useSchedulerDivisions'
import dayjs from 'dayjs'

interface DivisionDetailHeaderProps {
  division: DivisionItem
  onToggleReady: (divisionId: string) => void
}

const DivisionDetailHeader: React.FC<DivisionDetailHeaderProps> = ({
  division,
  onToggleReady,
}) => {
  const readyLabel = division.isReady ? 'Unmark Ready' : 'Mark as Ready'

  return (
    <div css={headerCard}>
      <div css={headerLeft}>
        <div css={nameRow}>
          <h2 css={divisionNameStyle}>{division.name}</h2>
          {division.isReady ? (
            <span css={readyBadge}>
              <i className='fas fa-check' css={badgeIcon} />
              Ready
            </span>
          ) : (
            <span css={draftBadge}>Draft</span>
          )}
        </div>
        {(division.lastModifiedByUserName || division.readyByUserName) && (
          <p css={modifiedLine}>
            <span>Last modified by </span>
            <span css={modifiedUserName}>
              {division.lastModifiedByUserName || division.readyByUserName}
            </span>
            {(division.lastModifiedAt || division.readyAt) && (
              <span>
                {' '}
                on{' '}
                {dayjs(division.lastModifiedAt || division.readyAt).format(
                  'MMMM D, YYYY, [at] h:mm A'
                )}
                .
              </span>
            )}
          </p>
        )}
      </div>
      <div css={buttonContainer}>
        <button css={readyButton} onClick={() => onToggleReady(division.id)}>
          <i className='fas fa-check' css={buttonIcon} />
          {readyLabel}
        </button>
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────

const headerCard = css`
  background-color: #212935;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const headerLeft = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const nameRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const divisionNameStyle = css`
  font-family: 'Barlow Condensed', 'Inter', sans-serif;
  font-size: 36px;
  font-weight: 600;
  line-height: 36px;
  color: ${colors.WHITE};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const readyBadge = css`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${colors.SUCCESS_200};
  background: rgba(5, 58, 17, 0.3);
  color: ${colors.SUCCESS_200};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 17.4px;
  white-space: nowrap;
`

const badgeIcon = css`
  font-size: 12px;
`

const draftBadge = css`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${colors.NEUTRAL_200};
  color: ${colors.NEUTRAL_70};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 17.4px;
  white-space: nowrap;
`

const modifiedLine = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_50};
  margin: 0;
  white-space: nowrap;
`

const modifiedUserName = css`
  font-weight: 500;
  color: ${colors.PRIMARY};
`

const buttonContainer = css`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
`

const readyButton = css`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background-color: ${colors.PRIMARY};
  border: none;
  border-radius: 8px;
  color: ${colors.PRIMARY_TEXT};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.PRIMARY_HOVER};
  }
`

const buttonIcon = css`
  font-size: 12px;
`

export default DivisionDetailHeader
